import { useEffect } from 'react';

import { StyledFlex, UncontrolledToggle } from '@dreryk/react-components';
import { Translate } from '@dreryk/react-common';

import { ElevenLabsTranscriptionConfig } from '../transcription/ElevenLabsTranscriptionConfig';
import { AcceptButton, RejectButton } from '../components/AcceptRejectButtons';
import { FieldSelector } from '../components/FieldSelector';
import { PartialPreview } from '../components/PartialPreview';
import { ProcessingIndicator } from '../components/ProcessingIndicator';
import { RecordButton } from '../components/RecordButton';
import { RedactButton } from '../components/RedactButton';
import { RedactionProposal } from '../components/RedactionProposal';
import { ResultBox } from '../components/ResultBox';
import { SilenceCountdown } from '../components/SilenceCountdown';
import { VoiceVisualizer } from '../components/VoiceVisualizer';
import { translationKeys } from '../translations';
import type { Field } from '../types';

import { useDictationPanel } from './DictationPanel.hooks';

type DictationPanelProps = {
   isQuotaExhausted: boolean;
   config: ElevenLabsTranscriptionConfig;
   onRecordingChange: (isRecording: boolean) => void;
   onAppendToField?: (field: Field, text: string) => void;
   getFieldHtml?: (field: Field) => string;
   setFieldHtml?: (field: Field, html: string) => void;
   setLockedField?: (field: Field | null) => void;
};

const noopGetFieldHtml = (): string => '';
const noopSetFieldHtml = (): void => undefined;

// eslint-disable-next-line complexity,max-lines-per-function
export const DictationPanel = (props: DictationPanelProps): JSX.Element => {
   const panel = useDictationPanel(
      props.config,
      props.onRecordingChange,
      props.getFieldHtml ?? noopGetFieldHtml,
      props.setFieldHtml ?? noopSetFieldHtml
   );

   const handleRecordClick = (): void => {
      if (panel.isRecording) {
         panel.transcription.stop();
      } else {
         panel.transcription.start(false, props.config);
      }
   };

   const isRedacting = panel.redaction.redactionState !== 'idle' && panel.redaction.redactionState !== 'error';
   const hasTranscript = panel.finalText.trim().length > 0;

   const { setLockedField } = props;
   useEffect(() => {
      setLockedField?.(panel.redactingField);
      return () => setLockedField?.(null);
   }, [panel.redactingField, setLockedField]);

   /*
    * The redact target toggle is always rendered (RedactButton is always visible).
    * It is disabled when there is no transcript — the user can only redact the visit
    * field in that case, so the toggle stays on 'visit-field'.
    */
   const handleRedactSourceChange = (transcriptActive: boolean): void => {
      panel.setRedactSource(transcriptActive ? 'transcript' : 'visit-field');
   };

   /*
    * The redact button is disabled only while loading. The visit field always has
    * content to redact (read live via ref on click); the transcript is only empty
    * when the user hasn't recorded anything yet.
    */
   const isRedactDisabled = panel.redactSource === 'transcript' && !hasTranscript;

   return (
      <StyledFlex direction={'column'} gap={12}>
         <FieldSelector value={panel.selectedField} disabled={panel.isRecording} onChange={panel.setSelectedField} />
         <VoiceVisualizer mediaStream={panel.transcription.mediaStream} isActive={panel.isRecording} />
         <SilenceCountdown secondsRemaining={panel.silenceSecondsRemaining} />
         <RecordButton
            recording={panel.isRecording}
            disabled={panel.isConnecting || props.isQuotaExhausted}
            onClick={handleRecordClick}
         />
         {panel.partialText || panel.isRecording ? <PartialPreview text={panel.partialText} /> : null}

         {panel.finalText ? (
            <ResultBox ref={panel.textareaRef} value={panel.finalText} onChange={panel.setFinalText} />
         ) : null}

         {/*
          * Accept/reject are shown after recording stops, outside of the redaction flow.
          * During redaction (loading/proposal), they are hidden — RedactionProposal takes over.
          */}
         {panel.finalText && !panel.isRecording && !isRedacting ? (
            <StyledFlex direction={'row'} gap={8} justifyContent={'flex-end'}>
               <AcceptButton
                  testId={'dictationAccept'}
                  onClick={() => panel.handleAccept(props.onAppendToField ?? (() => undefined))}
               />
               <RejectButton testId={'dictationReject'} onClick={panel.handleReject} />
            </StyledFlex>
         ) : null}

         {/*
          * Redact source toggle + button — always visible.
          * Toggle is disabled (stays on 'Pole z wizyty') when there is no transcript.
          * Text selection in the transcript textarea still works for partial redaction.
          */}
         {/* eslint-disable-next-line no-negated-condition */}
         {!isRedacting ? (
            <UncontrolledToggle
               testId={'redactSourceToggle'}
               active={panel.redactSource === 'transcript'}
               onActiveChange={handleRedactSourceChange}
               disabled={!hasTranscript || panel.isRecording || panel.isConnecting}
               asSwitch
               label={translationKeys.panel.redactButton}
               leftSlot={<Translate>{translationKeys.dictationMode.redactSourceVisitField}</Translate>}
               rightSlot={<Translate>{translationKeys.dictationMode.redactSourceTranscript}</Translate>}
            />
         ) : null}

         {/* eslint-disable-next-line no-negated-condition */}
         {!isRedacting ? (
            <RedactButton
               redactionState={panel.redaction.redactionState}
               disabled={isRedactDisabled || panel.isRecording || panel.isConnecting}
               onRedact={panel.redaction.redact}
            />
         ) : null}

         {panel.redaction.redactionState === 'loading' ? <ProcessingIndicator /> : null}

         {panel.redaction.redactionState === 'proposal' &&
         panel.redaction.redactedText &&
         panel.redaction.originalText ? (
            <RedactionProposal
               originalText={panel.redaction.originalText}
               redactedText={panel.redaction.redactedText}
               onAccept={panel.redaction.accept}
               onReject={panel.redaction.reject}
               onRetry={panel.redaction.retry}
            />
         ) : null}
      </StyledFlex>
   );
};
